import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExerciseMedia } from '@/components/ExerciseMedia';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2, RefreshCw, Info, Save, X, Loader2, AlertTriangle } from 'lucide-react';

const STRENGTH_EXCLUDED = ['walking', 'cardio', 'hiit', 'fitness racing', 'fitness-racing', 'hyrox', 'running'];
const isStrength = (e) => !STRENGTH_EXCLUDED.includes((e.category || '').toLowerCase());
const ENV_LABEL = { home: 'Home', gym: 'Gym', both: 'Home & Gym' };

function ExerciseDetail({ ex, onClose }) {
  return (
    <Dialog open={!!ex} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md" data-testid="review-exercise-detail">
        <DialogHeader><DialogTitle>{ex?.name}</DialogTitle></DialogHeader>
        {ex && (
          <div className="space-y-3 text-sm">
            {ex.gif_url && <ExerciseMedia src={ex.gif_url} alt={ex.name} className="w-full h-48 rounded-xl" />}
            <div className="text-xs text-muted-foreground">{[...new Set([ex.category, ex.muscle_group].filter(Boolean))].concat(ENV_LABEL[ex.training_environment] || []).join(' · ')}</div>
            {ex.description && <div><div className="label-mini mb-1">Instructions</div><p className="leading-relaxed whitespace-pre-line">{ex.description}</p></div>}
            {ex.coach_notes && <div><div className="label-mini mb-1">Coach notes</div><p className="leading-relaxed whitespace-pre-line">{ex.coach_notes}</p></div>}
            {!ex.description && !ex.coach_notes && <p className="text-muted-foreground">No instructions saved for this exercise yet.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditRows({ rows, setRows, library, sid }) {
  const [q, setQ] = useState('');
  const set = (i, patch) => setRows(r => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const replace = (i, v) => setRows(r => r.map((x, j) => (j === i ? { ...x, exercise_id: v, reps: /s$/i.test(String(x.reps || '').trim()) ? '10' : x.reps } : x)));
  const shown = (cur) => library.filter(e => e.id === cur || !q || e.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2">
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the exercise library…" data-testid={`review-search-${sid}`} className="h-8 text-sm bg-regen-card border-[var(--c-hair)]" />
      {rows.map((it, i) => (
        <div key={i} className="rounded-lg border border-[var(--c-hair)] p-2 space-y-2" data-testid={`review-edit-row-${sid}-${i}`}>
          <div className="flex gap-2 items-center">
            <Select value={it.exercise_id} onValueChange={(v) => replace(i, v)}>
              <SelectTrigger className="h-8 text-sm flex-1 bg-regen-card border-[var(--c-hair)]" data-testid={`review-replace-${sid}-${i}`}><SelectValue placeholder="Exercise" /></SelectTrigger>
              <SelectContent className="max-h-72">{shown(it.exercise_id).map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {ENV_LABEL[e.training_environment]}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => setRows(r => r.filter((_, j) => j !== i))} data-testid={`review-remove-${sid}-${i}`} className="text-[#FF5C5C] p-1"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[['sets', 'Sets'], ['reps', 'Reps'], ['rest_seconds', 'Rest (s)']].map(([k, label]) => (
              <label key={k} className="text-[10px] uppercase text-muted-foreground">{label}
                <Input value={it[k] ?? ''} onChange={e => set(i, { [k]: e.target.value })} data-testid={`review-${k}-${sid}-${i}`} className="h-8 mt-0.5 font-mono text-center bg-regen-card border-[var(--c-hair)]" />
              </label>
            ))}
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setRows(r => [...r, { exercise_id: library[0]?.id, sets: 3, reps: '10', rest_seconds: 60, notes: '' }])}
        data-testid={`review-add-${sid}`} className="w-full border-dashed border-[var(--c-hair)] text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add exercise</Button>
    </div>
  );
}

function Session({ s, sid, clientId, library, onSaved, onOpenEx }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/clients/${clientId}/training-review/workouts/${s.workout_id}`, { items: rows });
      toast.success('Session updated'); setRows(null); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-[var(--c-hair)]" data-testid={`review-session-${sid}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 p-3 text-left" data-testid={`review-session-toggle-${sid}`}>
        <div className="w-24 shrink-0 text-xs font-mono text-muted-foreground whitespace-nowrap">{format(parseISO(s.date), 'EEE d MMM')}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{s.name}</div>
          <div className="text-[11px] text-muted-foreground">{s.workout_type} · {s.items.length} exercise{s.items.length === 1 ? '' : 's'}{s.workout_format === 'Circuit' && s.rounds ? ` · ${s.rounds} rounds` : ''}</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {rows ? (
            <>
              <EditRows rows={rows} setRows={setRows} library={library} sid={sid} />
              <div className="flex gap-2">
                <Button size="sm" disabled={saving} onClick={save} data-testid={`review-save-${sid}`} className="flex-1 bg-[#00C8FF] text-[#081726]"><Save className="w-3.5 h-3.5 mr-1" /> Save session</Button>
                <Button size="sm" variant="outline" onClick={() => setRows(null)} className="border-[var(--c-hair)]"><X className="w-3.5 h-3.5" /></Button>
              </div>
            </>
          ) : (
            <>
              {s.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm py-1.5 border-b border-[var(--c-hair)] last:border-0" data-testid={`review-ex-${sid}-${i}`}>
                  <button onClick={() => onOpenEx(it.exercise)} className="flex-1 min-w-0 text-left flex items-center gap-1.5 hover:text-[#00C8FF]" data-testid={`review-ex-open-${sid}-${i}`}>
                    <Info className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{it.exercise?.name}</span>
                  </button>
                  <span className="font-mono text-xs shrink-0">{it.sets} × {it.reps}</span>
                  <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-16 text-right">{it.rest_seconds ?? 0}s rest</span>
                </div>
              ))}
              {s.editable
                ? <Button size="sm" variant="outline" onClick={() => setRows(s.items.map(({ exercise, ...x }) => x))} data-testid={`review-edit-${sid}`} className="w-full border-[var(--c-hair)]"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit session</Button>
                : <p className="text-[11px] text-muted-foreground">Coach JP circuit — edit it in the Workout Builder.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReviewHeader({ data, onRegenerate, busy }) {
  const p = data.profile, prog = data.program;
  const alloc = prog?.weekly_allocation ? Object.entries(prog.weekly_allocation).map(([t, n]) => `${n} ${t === 'Strength Training' ? 'Strength' : t}`).join(' + ') : '';
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
      <div className="flex-1">
        <div className="label-mini text-[#00C8FF]">Training programme {data.state === 'pending' ? '· awaiting your review' : data.state === 'active' ? '· active' : ''}</div>
        <div className="font-display font-bold text-lg mt-1" data-testid="review-program-name">{prog?.name || 'No programme yet'}</div>
        <div className="text-xs text-muted-foreground mt-1" data-testid="review-profile-summary">
          Profile: {p.goal || '—'} · {p.experience} · {p.sessions_per_week} sessions/week · {p.training_location} · Fitness Racing {p.fitness_racing ? 'yes' : 'no'}
        </div>
        {prog && <div className="text-xs mt-0.5" data-testid="review-allocation">Programme: {prog.experience} · {prog.sessions_per_week} sessions/week{alloc ? ` (${alloc})` : ''} · {prog.training_location}</div>}
      </div>
      <Button size="sm" variant="outline" disabled={busy} onClick={onRegenerate} data-testid="review-regenerate" className="border-[var(--c-hair)] shrink-0">
        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Generate again
      </Button>
    </div>
  );
}

export function TrainingReview({ clientId, onChanged, version }) {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(0);
  const [library, setLibrary] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get(`/admin/clients/${clientId}/training-review`).then(r => setData(r.data)).catch(() => setData(null)), [clientId]);
  useEffect(() => { load(); }, [load, version]);
  useEffect(() => { api.get('/exercises').then(r => setLibrary((r.data || []).filter(isStrength))).catch(() => {}); }, []);
  useEffect(() => {
    if (!data?.generating) return;
    const t = setInterval(() => { load(); onChanged?.(); }, 5000);
    return () => clearInterval(t);
  }, [data?.generating, load]);

  const regenerate = async () => {
    const msg = data.state === 'active'
      ? 'This client already has an active programme. Generate a NEW programme from the profile for your review? The active one stays until you approve the new one.'
      : 'Generate a new training programme from the client profile? The current unapproved programme will be replaced.';
    if (!window.confirm(msg)) return;
    try { await api.post(`/admin/clients/${clientId}/generate-programme`); toast.success('Generating from the client profile…'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start generation'); }
  };
  if (!data) return null;
  const home = data.profile.training_location === 'Home';
  const lib = home ? library.filter(e => e.training_environment !== 'gym') : library;
  const wk = data.weeks?.[week];
  return (
    <div className="regen-card-elevated p-5 space-y-4" data-testid="training-review">
      <ReviewHeader data={data} onRegenerate={regenerate} busy={data.generating} />
      {data.generating && <div className="flex items-center gap-2 text-sm text-[#00C8FF]" data-testid="review-generating"><Loader2 className="w-4 h-4 animate-spin" /> Generating the programme from the client profile…</div>}
      {data.generation_failed && !data.generating && (
        <div className="flex items-start gap-2 text-sm rounded-xl border border-[#FF5C5C]/40 bg-[#FF5C5C]/10 p-3" data-testid="review-failed">
          <AlertTriangle className="w-4 h-4 text-[#FF5C5C] shrink-0 mt-0.5" /> Automatic generation failed. Press "Generate again" to retry.
        </div>
      )}
      {data.weeks?.length > 0 && (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {data.weeks.map((w, i) => (
              <button key={w.week_number} onClick={() => setWeek(i)} data-testid={`review-week-${w.week_number}`}
                className={`px-3 py-1.5 rounded-full text-sm border shrink-0 transition-colors ${i === week ? 'bg-[#00C8FF] text-[#081726] border-[#00C8FF]' : 'border-[var(--c-hair)] hover:bg-[var(--c-film)]'}`}>
                Week {w.week_number} · {w.days.length}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {wk?.days.map((s, i) => <Session key={`${week}-${i}`} s={s} sid={`${week + 1}-${i}`} clientId={clientId} library={lib} onSaved={load} onOpenEx={setDetail} />)}
          </div>
        </>
      )}
      <ExerciseDetail ex={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
